import { DatePipe } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, forkJoin } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroCheck,
  heroEllipsisVertical,
  heroKey,
  heroMagnifyingGlass,
  heroNoSymbol,
  heroPlus,
  heroShieldCheck,
  heroTrash,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { NgpSelectPortal } from 'ng-primitives/select';
import type { AuthPermission } from '../../../core/api/generated/models/AuthPermission';
import { AuthStore } from '../../../core/auth/auth.store';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiCheckbox,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableActions,
  UiDataTableCell,
  UiDataTableEmptyCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiEmptyState,
  UiFilterActions,
  UiFilterBar,
  UiFilterField,
  UiFilterLabel,
  UiFilterSearch,
  UiHint,
  UiInput,
  UiLabel,
  UiMenuItem,
  UiMenuPanel,
  UiPanelCard,
  UiPanelText,
  UiRecordRow,
  UiSectionHeader,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiTextarea,
  UiPageHeader,
} from '../../../shared/ui';
import {
  SettingsCoreApiService,
  type CoreApiKey,
  type CoreMembership,
  type CorePermission,
  type CoreRole,
  type CoreRoleBinding,
  type CoreServiceAccount,
  type CoreTeam,
} from '../settings-core-api.service';

type SecurityTab = 'roles' | 'serviceAccounts';
type BindingTargetType = 'membership' | 'team' | 'serviceAccount';
type ServiceAccountKind = CoreServiceAccount['kind'];
type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

const SERVICE_ACCOUNT_KIND_OPTIONS: readonly { value: ServiceAccountKind; label: string }[] = [
  { value: 'integration', label: 'Integração' },
  { value: 'automation', label: 'Automação' },
  { value: 'webhook_inbound', label: 'Webhook inbound' },
  { value: 'system', label: 'Sistema' },
];

@Component({
  selector: 'app-security-settings-page',
  imports: [
    DatePipe,
    NgIcon,
    NgpMenuTrigger,
    NgpSelectPortal,
    ReactiveFormsModule,
    UiBadge,
    UiButton,
    UiCheckbox,
    UiDataTable,
    UiDataTableActions,
    UiDataTableCell,
    UiDataTableEmptyCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiEmptyState,
    UiFilterActions,
    UiFilterBar,
    UiFilterField,
    UiFilterLabel,
    UiFilterSearch,
    UiHint,
    UiInput,
    UiLabel,
    UiMenuItem,
    UiMenuPanel,
    UiPageHeader,
    UiPanelCard,
    UiPanelText,
    UiRecordRow,
    UiSectionHeader,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiTextarea,
  ],
  providers: [
    provideIcons({
      heroArrowPath,
      heroCheck,
      heroEllipsisVertical,
      heroKey,
      heroMagnifyingGlass,
      heroNoSymbol,
      heroPlus,
      heroShieldCheck,
      heroTrash,
      heroXMark,
    }),
  ],
  templateUrl: './security-settings.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecuritySettingsPage {
  private readonly api = inject(SettingsCoreApiService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastr = inject(ToastrService);

  readonly serviceAccountKindOptions = SERVICE_ACCOUNT_KIND_OPTIONS;
  readonly activeTab = signal<SecurityTab>(
    readSecurityTab(this.route.snapshot.data['settingsSection']),
  );
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly roles = signal<CoreRole[]>([]);
  readonly permissions = signal<CorePermission[]>([]);
  readonly memberships = signal<CoreMembership[]>([]);
  readonly teams = signal<CoreTeam[]>([]);
  readonly roleBindings = signal<CoreRoleBinding[]>([]);
  readonly serviceAccounts = signal<CoreServiceAccount[]>([]);
  readonly apiKeys = signal<CoreApiKey[]>([]);
  readonly createdRawKey = signal<string | null>(null);
  readonly createdRawKeyCopied = signal(false);
  readonly creatingRole = signal(false);
  readonly creatingServiceAccount = signal(false);
  readonly creatingApiKey = signal(false);
  readonly permissionSearchTerm = signal('');
  readonly permissionDomainFilter = signal('all');
  readonly editingRoleId = signal('');
  readonly deletingRoleId = signal('');
  readonly deleteRoleTransferId = signal('');
  readonly newRolePermissionKeys = signal<string[]>([]);
  readonly editedPermissionKeys = signal<string[]>([]);
  readonly serviceAccountRoleIds = signal<string[]>([]);
  readonly serviceAccountSavingIds = signal<string[]>([]);
  readonly apiKeySavingIds = signal<string[]>([]);
  readonly businessId = computed(() => this.authStore.business()?.id ?? null);
  readonly canCreateRoles = computed(() => this.hasPermission('core:roles:create'));
  readonly canUpdateRoles = computed(() => this.hasPermission('core:roles:update'));
  readonly canArchiveRoles = computed(() => this.hasPermission('core:roles:archive'));
  readonly canAssignRoles = computed(() => this.hasPermission('core:roles:assign'));
  readonly canCreateServiceAccounts = computed(() =>
    this.hasPermission('core:service-accounts:create'),
  );
  readonly canDisableServiceAccounts = computed(() =>
    this.hasPermission('core:service-accounts:disable'),
  );
  readonly canCreateApiKeys = computed(() => this.hasPermission('core:api-keys:create'));
  readonly canRevokeApiKeys = computed(() => this.hasPermission('core:api-keys:revoke'));
  readonly createRolePermissionHint =
    'Você precisa da permissão core:roles:create para criar funções.';
  readonly updateRolePermissionHint =
    'Você precisa da permissão core:roles:update para editar funções.';
  readonly archiveRolePermissionHint =
    'Você precisa da permissão core:roles:archive para excluir funções.';
  readonly assignRolePermissionHint =
    'Você precisa da permissão core:roles:assign para alterar atribuições.';
  readonly createServiceAccountPermissionHint =
    'Você precisa da permissão core:service-accounts:create para criar contas de serviço.';
  readonly disableServiceAccountPermissionHint =
    'Você precisa da permissão core:service-accounts:disable para desabilitar contas de serviço.';
  readonly createApiKeyPermissionHint =
    'Você precisa da permissão core:api-keys:create para gerar chaves.';
  readonly revokeApiKeyPermissionHint =
    'Você precisa da permissão core:api-keys:revoke para revogar chaves.';
  readonly assignableRoles = computed(() =>
    this.roles().filter((role) => roleVisibleInProduct(role) && !role.isSystem),
  );
  readonly visibleRoles = computed(() => this.roles().filter((role) => roleVisibleInProduct(role)));
  readonly visibleServiceAccounts = computed(() =>
    this.serviceAccounts().filter((account) => account.status !== 'archived'),
  );
  readonly serviceAccountsAvailableForKeys = computed(() =>
    this.visibleServiceAccounts().filter((account) => account.status === 'active'),
  );
  readonly visibleApiKeys = computed(() =>
    this.apiKeys().filter((apiKey) => apiKey.status !== 'archived'),
  );
  readonly editingRole = computed(
    () => this.roles().find((role) => role.id === this.editingRoleId()) ?? null,
  );
  readonly deletingRole = computed(
    () => this.roles().find((role) => role.id === this.deletingRoleId()) ?? null,
  );
  readonly deletingRoleBindings = computed(() =>
    this.roleBindings().filter((binding) => binding.roleId === this.deletingRoleId()),
  );
  readonly deleteRoleTransferOptions = computed(() =>
    this.visibleRoles().filter((role) => !role.isSystem && role.id !== this.deletingRoleId()),
  );
  readonly permissionDomains = computed(() =>
    [...new Set(this.permissions().map((permission) => permission.domain))].sort(),
  );
  readonly visiblePermissionDomains = computed(() => {
    const domainFilter = this.permissionDomainFilter();
    const search = this.normalizedPermissionSearch();
    return this.permissionDomains().filter((domain) => {
      if (domainFilter !== 'all' && domain !== domainFilter) return false;
      return this.permissions().some(
        (permission) => permission.domain === domain && permissionMatches(permission, search),
      );
    });
  });
  readonly newRoleSelectedCount = computed(() => this.newRolePermissionKeys().length);
  readonly editedRoleSelectedCount = computed(() => this.editedPermissionKeys().length);
  readonly selectedServiceAccountPermissionCount = computed(() => {
    const permissionKeys = new Set<string>();
    const selectedRoleIds = new Set(this.serviceAccountRoleIds());

    for (const role of this.assignableRoles()) {
      if (!selectedRoleIds.has(role.id)) continue;
      for (const permissionKey of role.permissionKeys ?? []) {
        if (!permissionKey.startsWith('platform-admin:')) {
          permissionKeys.add(permissionKey);
        }
      }
    }

    return permissionKeys.size;
  });
  readonly bindingTargets = computed(() => {
    const targetType = this.bindingForm.controls.targetType.value;
    if (targetType === 'team') {
      return this.teams().map((team) => ({ id: team.id, label: team.name }));
    }
    if (targetType === 'serviceAccount') {
      return this.serviceAccounts().map((account) => ({ id: account.id, label: account.name }));
    }

    return this.memberships().map((membership) => ({
      id: membership.id,
      label: membership.user?.displayName || membership.user?.primaryEmail || membership.id,
    }));
  });

  readonly roleForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: [''],
  });

  readonly editRoleForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: [''],
  });

  readonly permissionForm = this.fb.nonNullable.group({
    roleId: [''],
    permissionKey: [''],
  });

  readonly bindingForm = this.fb.nonNullable.group({
    roleId: [''],
    targetType: ['membership' as BindingTargetType],
    targetId: [''],
  });

  readonly serviceAccountForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: [''],
    kind: ['integration' as ServiceAccountKind],
  });

  readonly apiKeyForm = this.fb.nonNullable.group({
    serviceAccountId: [''],
    name: ['', [Validators.required, Validators.maxLength(120)]],
    expiresAt: [''],
  });

  constructor() {
    this.load();
  }

  selectTab(tab: SecurityTab) {
    this.activeTab.set(tab);
  }

  load() {
    const businessId = this.businessId();
    if (!businessId) return;

    this.loading.set(true);
    forkJoin({
      roles: this.api.listRoles(businessId),
      permissions: this.api.listPermissions(),
      memberships: this.api.listMemberships(businessId),
      teams: this.api.listTeams(businessId),
      roleBindings: this.api.listRoleBindings(businessId),
      serviceAccounts: this.api.listServiceAccounts(businessId),
      apiKeys: this.api.listApiKeys(businessId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({
          roles,
          permissions,
          memberships,
          teams,
          roleBindings,
          serviceAccounts,
          apiKeys,
        }) => {
          this.roles.set(roles.roles.filter((role) => role.status === 'active'));
          this.permissions.set(
            permissions.permissions.filter(
              (permission) => !permission.key.startsWith('platform-admin:'),
            ),
          );
          this.memberships.set(
            memberships.memberships.filter((membership) => membership.status === 'active'),
          );
          this.teams.set(teams.teams.filter((team) => team.status !== 'archived'));
          this.roleBindings.set(roleBindings.roleBindings);
          this.serviceAccounts.set(
            serviceAccounts.serviceAccounts.filter((account) => account.status !== 'archived'),
          );
          this.apiKeys.set(apiKeys.apiKeys.filter((apiKey) => apiKey.status !== 'archived'));
          this.ensureDefaults();
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toastr.error('Não foi possível carregar segurança.');
        },
      });
  }

  openCreateRole() {
    if (!this.requirePermission(this.canCreateRoles(), this.createRolePermissionHint)) return;

    this.roleForm.reset({ name: '', description: '' });
    this.newRolePermissionKeys.set([]);
    this.resetPermissionFilters();
    this.creatingRole.set(true);
  }

  closeCreateRole() {
    this.creatingRole.set(false);
    this.newRolePermissionKeys.set([]);
  }

  createRole() {
    const businessId = this.businessId();
    if (!businessId || this.roleForm.invalid) return;
    if (!this.requirePermission(this.canCreateRoles(), this.createRolePermissionHint)) return;

    const name = this.roleForm.controls.name.value.trim();
    this.saving.set(true);
    this.api
      .createRole({
        businessId,
        key: slugifyRoleKey(name),
        name,
        description: this.roleForm.controls.description.value.trim(),
        permissionKeys: this.newRolePermissionKeys(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (role) => {
          this.roles.update((roles) => [role, ...roles]);
          this.roleForm.reset({ name: '', description: '' });
          this.closeCreateRole();
          this.saving.set(false);
          this.toastr.success('Função criada.');
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          const { title, description } = describeRoleApiError(err);
          this.toastr.error(title, description);
        },
      });
  }

  newRolePermissionChecked(permissionKey: string) {
    return this.newRolePermissionKeys().includes(permissionKey);
  }

  toggleNewRolePermission(permissionKey: string, checked: boolean) {
    if (!this.canCreateRoles()) return;

    this.newRolePermissionKeys.update((keys) =>
      checked
        ? [...new Set([...keys, permissionKey])]
        : keys.filter((key) => key !== permissionKey),
    );
  }

  addPermissionToRole() {
    const roleId = this.permissionForm.controls.roleId.value;
    const permissionKey = this.permissionForm.controls.permissionKey.value;
    if (!this.requirePermission(this.canUpdateRoles(), this.updateRolePermissionHint)) return;
    if (!roleId || !permissionKey) {
      this.toastr.error('Selecione uma função e uma permissão.');
      return;
    }

    this.api
      .addRolePermission(roleId, permissionKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.roles.update((roles) =>
            roles.map((role) =>
              role.id === roleId
                ? {
                    ...role,
                    permissionKeys: [...new Set([...(role.permissionKeys ?? []), permissionKey])],
                  }
                : role,
            ),
          );
          this.toastr.success('Permissão adicionada.');
        },
        error: (err: HttpErrorResponse) => {
          const { title, description } = describeRoleApiError(err);
          this.toastr.error(title, description);
        },
      });
  }

  async removePermissionFromRole(roleId: string, permissionKey: string) {
    if (!this.requirePermission(this.canUpdateRoles(), this.updateRolePermissionHint)) return;

    const confirmed = await this.confirm.confirm({
      title: 'Remover permissão?',
      description: 'A função deixará de conceder esta permissão aos usuários associados.',
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.api
      .removeRolePermission(roleId, permissionKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.roles.update((roles) =>
            roles.map((role) =>
              role.id === roleId
                ? {
                    ...role,
                    permissionKeys: (role.permissionKeys ?? []).filter(
                      (key) => key !== permissionKey,
                    ),
                  }
                : role,
            ),
          );
          this.toastr.success('Permissão removida.');
        },
        error: () => this.toastr.error('Não foi possível remover a permissão.'),
      });
  }

  openEditRole(role: CoreRole) {
    if (role.isSystem) return;
    if (!this.requirePermission(this.canUpdateRoles(), this.updateRolePermissionHint)) return;

    this.editingRoleId.set(role.id);
    this.resetPermissionFilters();
    this.editRoleForm.patchValue({
      name: role.name,
      description: role.description ?? '',
    });
    this.editedPermissionKeys.set(
      (role.permissionKeys ?? []).filter((key) => !key.startsWith('platform-admin:')),
    );
  }

  closeEditRole() {
    this.editingRoleId.set('');
    this.editedPermissionKeys.set([]);
  }

  editedPermissionChecked(permissionKey: string) {
    return this.editedPermissionKeys().includes(permissionKey);
  }

  toggleEditedPermission(permissionKey: string, checked: boolean) {
    if (!this.canUpdateRoles()) return;

    this.editedPermissionKeys.update((keys) =>
      checked
        ? [...new Set([...keys, permissionKey])]
        : keys.filter((key) => key !== permissionKey),
    );
  }

  saveEditedRole() {
    const role = this.editingRole();
    if (!role || this.editRoleForm.invalid) return;
    if (!this.requirePermission(this.canUpdateRoles(), this.updateRolePermissionHint)) return;

    const current = new Set(
      (role.permissionKeys ?? []).filter((key) => !key.startsWith('platform-admin:')),
    );
    const next = new Set(this.editedPermissionKeys());
    const additions = [...next].filter((key) => !current.has(key));
    const removals = [...current].filter((key) => !next.has(key));

    this.saving.set(true);
    void Promise.resolve()
      .then(() =>
        this.api
          .updateRole(role.id, {
            name: this.editRoleForm.controls.name.value.trim(),
            description: this.editRoleForm.controls.description.value.trim(),
          })
          .pipe(takeUntilDestroyed(this.destroyRef)),
      )
      .then((request) => firstValueFrom(request))
      .then(() =>
        Promise.all(
          additions.map((key) => firstValueFrom(this.api.addRolePermission(role.id, key))),
        ),
      )
      .then(() =>
        Promise.all(
          removals.map((key) => firstValueFrom(this.api.removeRolePermission(role.id, key))),
        ),
      )
      .then(() => {
        this.roles.update((roles) =>
          roles.map((item) =>
            item.id === role.id
              ? {
                  ...item,
                  name: this.editRoleForm.controls.name.value.trim(),
                  description: this.editRoleForm.controls.description.value.trim(),
                  permissionKeys: [...next],
                }
              : item,
          ),
        );
        this.saving.set(false);
        this.closeEditRole();
        this.toastr.success('Função atualizada.');
      })
      .catch((err: HttpErrorResponse) => {
        this.saving.set(false);
        const { title, description } = describeRoleApiError(err);
        this.toastr.error(title, description);
      });
  }

  openDeleteRole(role: CoreRole) {
    if (role.isSystem) return;
    if (!this.requirePermission(this.canArchiveRoles(), this.archiveRolePermissionHint)) return;

    this.deletingRoleId.set(role.id);
    this.deleteRoleTransferId.set('');
  }

  closeDeleteRole() {
    this.deletingRoleId.set('');
    this.deleteRoleTransferId.set('');
  }

  selectDeleteRoleTransfer(value: unknown) {
    this.deleteRoleTransferId.set(readSelectString(value));
  }

  deleteRoleTransferLabel() {
    const roleId = this.deleteRoleTransferId();
    return (
      this.deleteRoleTransferOptions().find((role) => role.id === roleId)?.name ??
      'Remover atribuições'
    );
  }

  async confirmDeleteRole() {
    const role = this.deletingRole();
    if (!role || role.isSystem) return;
    if (!this.requirePermission(this.canArchiveRoles(), this.archiveRolePermissionHint)) return;

    const bindings = this.roleBindings().filter((binding) => binding.roleId === role.id);
    if (
      bindings.length > 0 &&
      !this.requirePermission(this.canAssignRoles(), this.assignRolePermissionHint)
    ) {
      return;
    }

    const targetRole = this.deleteRoleTransferId()
      ? this.deleteRoleTransferOptions().find((item) => item.id === this.deleteRoleTransferId())
      : null;

    this.saving.set(true);
    void this.transferDeletingRoleBindings(bindings, targetRole ?? null)
      .then(() =>
        Promise.all(
          bindings.map((binding) => firstValueFrom(this.api.deleteRoleBinding(binding.id))),
        ),
      )
      .then(() => firstValueFrom(this.api.archiveRole(role.id)))
      .then(() => {
        this.roles.update((roles) => roles.filter((item) => item.id !== role.id));
        this.roleBindings.update((items) => items.filter((item) => item.roleId !== role.id));
        this.closeDeleteRole();
        this.saving.set(false);
        this.toastr.success('Função excluída.');
      })
      .catch(() => {
        this.saving.set(false);
        this.toastr.error('Não foi possível excluir a função.');
      });
  }

  private async transferDeletingRoleBindings(
    bindings: CoreRoleBinding[],
    targetRole: CoreRole | null,
  ) {
    if (!targetRole) return;
    const existing = new Set(
      this.roleBindings()
        .filter((binding) => binding.roleId === targetRole.id)
        .map((binding) => bindingPrincipalKey(binding)),
    );
    const created = await Promise.all(
      bindings
        .filter((binding) => !existing.has(bindingPrincipalKey(binding)))
        .map((binding) =>
          firstValueFrom(
            this.api.createRoleBinding({
              businessId: binding.businessId,
              roleId: targetRole.id,
              ...(binding.membershipId ? { membershipId: binding.membershipId } : {}),
              ...(binding.teamId ? { teamId: binding.teamId } : {}),
              ...(binding.serviceAccountId ? { serviceAccountId: binding.serviceAccountId } : {}),
            }),
          ),
        ),
    );
    this.roleBindings.update((items) => [...created, ...items]);
  }

  permissionCount(role: CoreRole) {
    return (role.permissionKeys ?? []).filter((key) => !key.startsWith('platform-admin:')).length;
  }

  userCountForRole(roleId: string) {
    return this.roleBindings().filter(
      (binding) => binding.roleId === roleId && binding.membershipId,
    ).length;
  }

  createRoleBinding() {
    const businessId = this.businessId();
    const roleId = this.bindingForm.controls.roleId.value;
    const targetId = this.bindingForm.controls.targetId.value;
    const targetType = this.bindingForm.controls.targetType.value;
    if (!businessId || !roleId || !targetId) {
      this.toastr.error('Selecione função e alvo.');
      return;
    }
    if (!this.requirePermission(this.canAssignRoles(), this.assignRolePermissionHint)) return;

    this.api
      .createRoleBinding({
        businessId,
        roleId,
        ...(targetType === 'membership' ? { membershipId: targetId } : {}),
        ...(targetType === 'team' ? { teamId: targetId } : {}),
        ...(targetType === 'serviceAccount' ? { serviceAccountId: targetId } : {}),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (binding) => {
          this.roleBindings.update((bindings) => [binding, ...bindings]);
          this.toastr.success('Função atribuída.');
        },
        error: () => this.toastr.error('Não foi possível atribuir a função.'),
      });
  }

  async deleteRoleBinding(bindingId: string) {
    if (!this.requirePermission(this.canAssignRoles(), this.assignRolePermissionHint)) return;

    const confirmed = await this.confirm.confirm({
      title: 'Remover atribuição?',
      description: 'O alvo deixa de receber as permissões desta função.',
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.api
      .deleteRoleBinding(bindingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.roleBindings.update((bindings) =>
            bindings.filter((binding) => binding.id !== bindingId),
          );
          this.toastr.success('Atribuição removida.');
        },
        error: () => this.toastr.error('Não foi possível remover a atribuição.'),
      });
  }

  openCreateServiceAccount() {
    if (
      !this.requirePermission(
        this.canCreateServiceAccounts(),
        this.createServiceAccountPermissionHint,
      )
    ) {
      return;
    }

    this.serviceAccountForm.reset({ name: '', description: '', kind: 'integration' });
    this.serviceAccountRoleIds.set([]);
    this.creatingServiceAccount.set(true);
  }

  closeCreateServiceAccount() {
    this.creatingServiceAccount.set(false);
    this.serviceAccountRoleIds.set([]);
  }

  async createServiceAccount() {
    const businessId = this.businessId();
    if (!businessId || this.serviceAccountForm.invalid) return;
    if (
      !this.requirePermission(
        this.canCreateServiceAccounts(),
        this.createServiceAccountPermissionHint,
      )
    ) {
      return;
    }
    if (!this.requirePermission(this.canAssignRoles(), this.assignRolePermissionHint)) return;
    if (this.serviceAccountRoleIds().length === 0) {
      this.toastr.error('Selecione pelo menos uma função para a conta de serviço.');
      return;
    }

    this.saving.set(true);
    try {
      const account = await firstValueFrom(
        this.api
          .createServiceAccount({
            businessId,
            name: this.serviceAccountForm.controls.name.value.trim(),
            description: this.serviceAccountForm.controls.description.value.trim(),
            kind: this.serviceAccountForm.controls.kind.value,
          })
          .pipe(takeUntilDestroyed(this.destroyRef)),
      );
      const bindings = await Promise.all(
        this.serviceAccountRoleIds().map((roleId) =>
          firstValueFrom(
            this.api
              .createRoleBinding({
                businessId,
                roleId,
                serviceAccountId: account.id,
              })
              .pipe(takeUntilDestroyed(this.destroyRef)),
          ),
        ),
      );

      this.serviceAccounts.update((accounts) => [account, ...accounts]);
      this.roleBindings.update((items) => [...bindings, ...items]);
      this.apiKeyForm.controls.serviceAccountId.setValue(account.id);
      this.serviceAccountForm.reset({ name: '', description: '', kind: 'integration' });
      this.closeCreateServiceAccount();
      this.saving.set(false);
      this.toastr.success('Conta de serviço criada.');
    } catch {
      this.saving.set(false);
      this.toastr.error('Não foi possível criar a conta de serviço.');
    }
  }

  async disableServiceAccount(account: CoreServiceAccount) {
    if (
      !this.requirePermission(
        this.canDisableServiceAccounts(),
        this.disableServiceAccountPermissionHint,
      )
    ) {
      return;
    }

    const confirmed = await this.confirm.confirm({
      title: 'Desabilitar conta de serviço?',
      description: `${account.name} tem ${this.serviceAccountKeyCount(account.id)} chaves ativas e ${this.serviceAccountRoleCount(account.id)} funções. Integrações e automações que usam esta conta podem parar de funcionar.`,
      confirmLabel: 'Desabilitar',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.markServiceAccountSaving(account.id, true);
    this.api
      .disableServiceAccount(account.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.serviceAccounts.update((accounts) =>
            accounts.map((item) => (item.id === updated.id ? updated : item)),
          );
          this.markServiceAccountSaving(account.id, false);
          this.toastr.success('Conta de serviço desabilitada.');
        },
        error: () => {
          this.markServiceAccountSaving(account.id, false);
          this.toastr.error('Não foi possível desabilitar a conta de serviço.');
        },
      });
  }

  openCreateApiKey() {
    if (!this.requirePermission(this.canCreateApiKeys(), this.createApiKeyPermissionHint)) return;

    const firstAvailableAccount = this.serviceAccountsAvailableForKeys()[0]?.id ?? '';
    this.apiKeyForm.reset({
      serviceAccountId: this.apiKeyForm.controls.serviceAccountId.value || firstAvailableAccount,
      name: '',
      expiresAt: '',
    });
    this.createdRawKey.set(null);
    this.createdRawKeyCopied.set(false);
    this.creatingApiKey.set(true);
  }

  closeCreateApiKey() {
    this.creatingApiKey.set(false);
  }

  createApiKey() {
    const businessId = this.businessId();
    const serviceAccountId = this.apiKeyForm.controls.serviceAccountId.value;
    if (!businessId || !serviceAccountId || this.apiKeyForm.invalid) {
      this.toastr.error('Selecione a conta de serviço e informe o nome da chave.');
      return;
    }
    if (
      !this.serviceAccountsAvailableForKeys().some((account) => account.id === serviceAccountId)
    ) {
      this.toastr.error('Selecione uma conta de serviço ativa.');
      return;
    }
    if (!this.requirePermission(this.canCreateApiKeys(), this.createApiKeyPermissionHint)) return;

    this.saving.set(true);
    this.api
      .createApiKey({
        businessId,
        serviceAccountId,
        name: this.apiKeyForm.controls.name.value.trim(),
        expiresAt: this.apiKeyForm.controls.expiresAt.value || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (apiKey) => {
          this.createdRawKey.set(apiKey.rawKey);
          this.createdRawKeyCopied.set(false);
          this.apiKeys.update((keys) => [apiKey, ...keys]);
          this.apiKeyForm.controls.name.setValue('');
          this.closeCreateApiKey();
          this.saving.set(false);
          this.toastr.success('Chave de API criada.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível criar a chave de API.');
        },
      });
  }

  async revokeApiKey(apiKey: CoreApiKey) {
    if (!this.requirePermission(this.canRevokeApiKeys(), this.revokeApiKeyPermissionHint)) return;

    const confirmed = await this.confirm.confirm({
      title: 'Revogar chave de API?',
      description: `${apiKey.name} (${apiKey.keyPrefix}) perde acesso imediatamente. Sistemas usando esta chave começam a falhar na próxima requisição.`,
      confirmLabel: 'Revogar',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.markApiKeySaving(apiKey.id, true);
    this.api
      .revokeApiKey(apiKey.id, 'Revogada pela tela de Segurança')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.apiKeys.update((keys) =>
            keys.map((item) => (item.id === updated.id ? updated : item)),
          );
          this.markApiKeySaving(apiKey.id, false);
          this.toastr.success('Chave de API revogada.');
        },
        error: () => {
          this.markApiKeySaving(apiKey.id, false);
          this.toastr.error('Não foi possível revogar a chave de API.');
        },
      });
  }

  selectRoleForPermission(value: unknown) {
    this.permissionForm.controls.roleId.setValue(readSelectString(value));
  }

  selectPermission(value: unknown) {
    this.permissionForm.controls.permissionKey.setValue(readSelectString(value));
  }

  selectBindingRole(value: unknown) {
    this.bindingForm.controls.roleId.setValue(readSelectString(value));
  }

  selectBindingTargetType(value: unknown) {
    this.bindingForm.controls.targetType.setValue(readBindingTargetType(value));
    this.bindingForm.controls.targetId.setValue('');
  }

  selectBindingTarget(value: unknown) {
    this.bindingForm.controls.targetId.setValue(readSelectString(value));
  }

  selectServiceAccountKind(value: unknown) {
    this.serviceAccountForm.controls.kind.setValue(readServiceAccountKind(value));
  }

  selectServiceAccountRoles(value: unknown) {
    if (!this.canAssignRoles()) return;
    this.serviceAccountRoleIds.set(readSelectStringArray(value));
  }

  selectApiKeyServiceAccount(value: unknown) {
    this.apiKeyForm.controls.serviceAccountId.setValue(readSelectString(value));
  }

  permissionsForDomain(domain: string) {
    const search = this.normalizedPermissionSearch();
    return this.permissions().filter(
      (permission) => permission.domain === domain && permissionMatches(permission, search),
    );
  }

  setPermissionSearchTerm(value: string) {
    this.permissionSearchTerm.set(value);
  }

  selectPermissionDomain(value: unknown) {
    this.permissionDomainFilter.set(readSelectString(value) || 'all');
  }

  permissionDomainLabel(domain: string) {
    return domain === 'all' ? 'Todos os domínios' : domain;
  }

  selectAllNewRoleDomain(domain: string) {
    if (!this.canCreateRoles()) return;
    const keys = this.permissionsForDomain(domain).map((permission) => permission.key);
    this.newRolePermissionKeys.update((current) => [...new Set([...current, ...keys])]);
  }

  clearNewRoleDomain(domain: string) {
    if (!this.canCreateRoles()) return;
    const keys = new Set(this.permissionsForDomain(domain).map((permission) => permission.key));
    this.newRolePermissionKeys.update((current) => current.filter((key) => !keys.has(key)));
  }

  selectAllEditedRoleDomain(domain: string) {
    if (!this.canUpdateRoles()) return;
    const keys = this.permissionsForDomain(domain).map((permission) => permission.key);
    this.editedPermissionKeys.update((current) => [...new Set([...current, ...keys])]);
  }

  clearEditedRoleDomain(domain: string) {
    if (!this.canUpdateRoles()) return;
    const keys = new Set(this.permissionsForDomain(domain).map((permission) => permission.key));
    this.editedPermissionKeys.update((current) => current.filter((key) => !keys.has(key)));
  }

  async copyCreatedRawKey() {
    const rawKey = this.createdRawKey();
    if (!rawKey) return;

    try {
      await navigator.clipboard.writeText(rawKey);
      this.createdRawKeyCopied.set(true);
      this.toastr.success('Chave copiada.');
    } catch {
      this.toastr.error('Não foi possível copiar a chave automaticamente.');
    }
  }

  dismissCreatedRawKey() {
    if (!this.createdRawKeyCopied()) {
      this.toastr.error('Copie a chave antes de dispensar este aviso.');
      return;
    }

    this.createdRawKey.set(null);
    this.createdRawKeyCopied.set(false);
  }

  roleLabel(roleId: string) {
    return this.roles().find((role) => role.id === roleId)?.name ?? 'Selecionar função';
  }

  roleSelectionLabel(roleIds: string[]) {
    if (!roleIds.length) return 'Selecionar funções';
    return roleIds.map((roleId) => this.roleLabel(roleId)).join(', ');
  }

  permissionLabel(permissionKey: string) {
    return (
      this.permissions().find((permission) => permission.key === permissionKey)?.key ??
      'Selecionar permissão'
    );
  }

  serviceAccountLabel(serviceAccountId: string) {
    return (
      this.serviceAccounts().find((account) => account.id === serviceAccountId)?.name ??
      'Selecionar conta de serviço'
    );
  }

  serviceAccountKindLabel(kind: ServiceAccountKind) {
    return SERVICE_ACCOUNT_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
  }

  serviceAccountStatusLabel(status: string) {
    if (status === 'active') return 'Ativa';
    if (status === 'disabled') return 'Desabilitada';
    if (status === 'archived') return 'Arquivada';
    return 'Pendente';
  }

  serviceAccountStatusTone(status: string): BadgeTone {
    if (status === 'active') return 'success';
    if (status === 'disabled') return 'warning';
    return 'neutral';
  }

  apiKeyStatusLabel(status: string) {
    if (status === 'active') return 'Ativa';
    if (status === 'revoked') return 'Revogada';
    if (status === 'expired') return 'Expirada';
    if (status === 'archived') return 'Arquivada';
    return 'Pendente';
  }

  apiKeyStatusTone(status: string): BadgeTone {
    if (status === 'active') return 'success';
    if (status === 'revoked' || status === 'expired') return 'danger';
    return 'neutral';
  }

  serviceAccountKeyCount(serviceAccountId: string) {
    return this.visibleApiKeys().filter(
      (apiKey) => apiKey.serviceAccountId === serviceAccountId && apiKey.status === 'active',
    ).length;
  }

  serviceAccountRoleCount(serviceAccountId: string) {
    return this.roleBindings().filter((binding) => binding.serviceAccountId === serviceAccountId)
      .length;
  }

  targetTypeLabel(type: BindingTargetType) {
    if (type === 'team') return 'Equipe';
    if (type === 'serviceAccount') return 'Conta de serviço';
    return 'Membro';
  }

  selectedBindingTargetLabel() {
    const targetId = this.bindingForm.controls.targetId.value;
    return (
      this.bindingTargets().find((target) => target.id === targetId)?.label ?? 'Selecionar alvo'
    );
  }

  bindingTargetLabel(binding: CoreRoleBinding) {
    if (binding.membershipId) {
      const membership = this.memberships().find((item) => item.id === binding.membershipId);
      return (
        membership?.user?.displayName || membership?.user?.primaryEmail || binding.membershipId
      );
    }

    if (binding.teamId) {
      return this.teams().find((team) => team.id === binding.teamId)?.name ?? binding.teamId;
    }

    if (binding.serviceAccountId) {
      return (
        this.serviceAccounts().find((account) => account.id === binding.serviceAccountId)?.name ??
        binding.serviceAccountId
      );
    }

    return 'Alvo não identificado';
  }

  bindingTargetType(binding: CoreRoleBinding) {
    if (binding.teamId) return 'Equipe';
    if (binding.serviceAccountId) return 'Conta de serviço';
    return 'Membro';
  }

  deletingRoleImpactLabel() {
    const bindings = this.deletingRoleBindings();
    const users = bindings.filter((binding) => binding.membershipId).length;
    const teams = bindings.filter((binding) => binding.teamId).length;
    const serviceAccounts = bindings.filter((binding) => binding.serviceAccountId).length;

    if (!bindings.length) return 'Nenhuma atribuição usa esta função.';

    return `${bindings.length} atribuições usam esta função: ${users} usuários, ${teams} equipes e ${serviceAccounts} contas de serviço.`;
  }

  isServiceAccountSaving(serviceAccountId: string) {
    return this.serviceAccountSavingIds().includes(serviceAccountId);
  }

  isApiKeySaving(apiKeyId: string) {
    return this.apiKeySavingIds().includes(apiKeyId);
  }

  private ensureDefaults() {
    const firstRole = this.assignableRoles()[0];
    if (firstRole) {
      this.permissionForm.controls.roleId.setValue(
        this.permissionForm.controls.roleId.value || firstRole.id,
      );
      this.bindingForm.controls.roleId.setValue(
        this.bindingForm.controls.roleId.value || firstRole.id,
      );
    }

    const firstPermission = this.permissions()[0];
    if (firstPermission) {
      this.permissionForm.controls.permissionKey.setValue(
        this.permissionForm.controls.permissionKey.value || firstPermission.key,
      );
    }

    const firstTarget = this.bindingTargets()[0];
    if (firstTarget) {
      this.bindingForm.controls.targetId.setValue(
        this.bindingForm.controls.targetId.value || firstTarget.id,
      );
    }

    const firstServiceAccount = this.serviceAccountsAvailableForKeys()[0];
    if (firstServiceAccount) {
      this.apiKeyForm.controls.serviceAccountId.setValue(
        this.apiKeyForm.controls.serviceAccountId.value || firstServiceAccount.id,
      );
    }
  }

  private hasPermission(permission: AuthPermission) {
    return this.authStore.hasEveryPermission([permission]);
  }

  private requirePermission(allowed: boolean, message: string) {
    if (allowed) return true;
    this.toastr.error(message);
    return false;
  }

  private resetPermissionFilters() {
    this.permissionSearchTerm.set('');
    this.permissionDomainFilter.set('all');
  }

  private normalizedPermissionSearch() {
    return this.permissionSearchTerm().trim().toLocaleLowerCase('pt-BR');
  }

  private markServiceAccountSaving(serviceAccountId: string, saving: boolean) {
    this.serviceAccountSavingIds.update((ids) =>
      saving
        ? [...new Set([...ids, serviceAccountId])]
        : ids.filter((id) => id !== serviceAccountId),
    );
  }

  private markApiKeySaving(apiKeyId: string, saving: boolean) {
    this.apiKeySavingIds.update((ids) =>
      saving ? [...new Set([...ids, apiKeyId])] : ids.filter((id) => id !== apiKeyId),
    );
  }
}

function readSelectString(value: unknown) {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }

  return typeof value === 'string' ? value : '';
}

function readSelectStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return typeof value === 'string' && value ? [value] : [];
}

function bindingPrincipalKey(binding: CoreRoleBinding) {
  return `${binding.membershipId ?? ''}:${binding.teamId ?? ''}:${binding.serviceAccountId ?? ''}`;
}

function roleVisibleInProduct(role: CoreRole) {
  return (
    role.status === 'active' &&
    !(role.permissionKeys ?? []).some((key) => key.startsWith('platform-admin:'))
  );
}

function readBindingTargetType(value: unknown): BindingTargetType {
  const selected = readSelectString(value);
  if (selected === 'team' || selected === 'serviceAccount') return selected;
  return 'membership';
}

function readServiceAccountKind(value: unknown): ServiceAccountKind {
  const selected = readSelectString(value);
  return SERVICE_ACCOUNT_KIND_OPTIONS.some((option) => option.value === selected)
    ? (selected as ServiceAccountKind)
    : 'integration';
}

function readSecurityTab(value: unknown): SecurityTab {
  return value === 'serviceAccounts' ? 'serviceAccounts' : 'roles';
}

function slugifyRoleKey(value: string) {
  return (
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50) || 'funcao'
  );
}

/** Nomes amigáveis pra permissões owner-exclusive (backend: role.service.ts::isForbiddenForCustomRole,
 *  W2-5) — a mensagem crua do backend vem em inglês e cita a chave técnica; aqui devolvemos o
 *  motivo em PT-BR. Chaves fora desse mapa (ex.: platform-admin:*) caem no fallback genérico. */
const OWNER_EXCLUSIVE_PERMISSION_LABEL: Record<string, string> = {
  'core:owner:assign': 'atribuir o papel de owner',
  'core:owner:remove': 'remover o papel de owner',
  'core:business:update': 'renomear ou mudar o status da empresa',
};

/** Traduz o erro da API de roles pra uma mensagem acionável em PT-BR. Cobre o código que o
 *  backend já devolve (`error.error`) — sem isso, toda falha de criar/editar role virava um
 *  toast genérico "não foi possível", mesmo quando o motivo (permissão exclusiva de owner) é
 *  claro e o usuário poderia corrigir só desmarcando o item certo. */
function describeRoleApiError(err: HttpErrorResponse): { title: string; description?: string } {
  const body = err.error as { error?: string; message?: string; details?: { keys?: string[] } } | null;
  const code = body?.error;

  if (code === 'forbidden_custom_permission') {
    const keys = body?.details?.keys ?? [];
    const described = keys.map((key) => OWNER_EXCLUSIVE_PERMISSION_LABEL[key] ?? key);
    return {
      title: 'Permissão exclusiva de owner',
      description: described.length
        ? `Uma role customizada não pode ${described.join(', ')} — isso é exclusivo do papel de sistema Owner. Desmarque essa permissão e tente de novo.`
        : 'Uma das permissões selecionadas é exclusiva do papel de sistema Owner e não pode ir para uma role customizada.',
    };
  }
  if (code === 'reserved_role_key') {
    return { title: 'Nome reservado', description: 'Esse nome de função é reservado para papéis do sistema — escolha outro.' };
  }
  if (code === 'custom_role_conflict') {
    return { title: 'Função já existe', description: 'Já existe uma função com esse nome neste negócio — escolha outro.' };
  }
  if (code === 'system_role_immutable') {
    return { title: 'Papel do sistema', description: 'Papéis de sistema (Owner, Admin, Manager, Member, Viewer) não podem ser editados.' };
  }
  return { title: 'Não foi possível concluir', description: body?.message };
}

function permissionMatches(permission: CorePermission, search: string) {
  if (!search) return true;

  return `${permission.key} ${permission.description} ${permission.domain}`
    .toLocaleLowerCase('pt-BR')
    .includes(search);
}
